/* eslint-disable react-hooks/exhaustive-deps */
import {Snackbar, Typography} from '@mui/material'
import React, {useEffect, useState} from 'react'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import TableCustom, {PaginationType, TableColumn} from 'src/components/elements/TableCustom/TableCustom'
import {Action} from 'src/components/elements/Menus/ActionMenu/ActionsMenu'

import Filters, {FilterItem} from 'src/components/templates/Filters/Filters'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {saveBlob} from 'src/utils/medicalReportFile'
import {useAppSelector} from 'src/hooks'
import EchocardiographicRequestDialog from 'src/components/templates/EchocardiographicRequestDialog/EchocardiographicRequestDialog'
import {mdiDownload, mdiOpenInNew} from '@mdi/js'
import FileViewerModal from 'src/components/elements/FileViewerModal/FileViewerModal'

const Exams = ({patientId}: {patientId: string}) => {
  const examsColumnOptions = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.examsConfiguration?.columnOptions
  )

  const DEFAULT_COLUMNS : TableColumn[] = [
    {
      id: 'date',
      label: 'DATA ESAME',
      type: 'date',
      format: 'ddd DD MMM YYYY HH:mm'
    },
    {
      id: 'type',
      label: 'TIPO ESAME',
      render: (value: string) => {
        switch (value) {
          case 'eco':
            return 'ECO'
          default:
            return value
        }
      }
    },
    {
      id: 'requestingUser',
      label: 'MEDICO RICHIEDENTE',
      noSort: true
    },
    {
      id: 'reportingDoctor',
      label: 'MEDICO REFERTANTE',
      noSort: true
    },
    {
      id: 'state',
      label: 'STATO REFERTO',
      noSort: true
    }
  ]

  const columns : TableColumn[] = examsColumnOptions?.length > 0 ? examsColumnOptions : DEFAULT_COLUMNS

  const statusOptions = [
    {
      value: 'NOT_COMPLETED',
      label: 'Assente'
    },
    {
      value: 'COMPLETED',
      label: 'Completato'
    }
  ]

  const filterConfig: FilterItem[] = [
    {
      id: 'type',
      label: 'Tipo esame',
      type: 'select',
      options: [{value: 'eco', label: 'ECO'}]
    },
    {
      id: 'rangeDates',
      label: 'Data',
      type: 'rangePicker'
    },
    {
      id: 'reportStatus',
      label: 'Stato referto',
      type: 'select',
      options: statusOptions
    }
  ]

  const [filters, setFilters] = useState<Record<string, string | string[]>[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<any>(undefined)
  const [statusCount, setStatusCount] = useState<any>(undefined)
  const [isRequestModal, setIsRequestModal] = useState<boolean>(false)
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: '-date'
  })
  const [statusDelete, setStatusDelete] = useState<any>(undefined)
  const [statusPost, setStatusPost] = useState<any>(undefined)
  const [postMessage, setPostMessage] = useState<any>(undefined)
  const [openViewModal, setOpenViewModal] = useState<boolean>(false)
  const [MIMETypeValue, setMIMETypeValue] = useState<any>()
  const [file, setFile] = useState<any>()
  const [fileName, setFileName] = useState<any>()
  const [statusDownload, setStatusDownload] = useState<any>(undefined)
  const [statusViewer, setStatusViewer] = useState<any>(undefined)
  const [viewerMessage, setViewerMessage] = useState<any>(undefined)

  const handleOpenEcoExam = async (row: any) => {
    setViewerMessage("Errore nel richiedere l'esame")

    const response = await apiClient.getDicomToken(patientId, row._id, row.studyUID, setStatusViewer)
    const link = `https://view.miacare.zeeromed.cloud/ngv/?q=${response?.token}`

    window.open(link, '_blank')
  }

  const handleModalView = async (row: any) => {
    setOpenViewModal(true)
    const report = await apiClient.downloadMedicalReport(patientId, row._id, setStatusDownload)
    if (!report) return
    // Solo i PDF hanno l'anteprima: gli altri formati si scaricano con il nome originale
    if (!report.isPdf) {
      setOpenViewModal(false)
      saveBlob(report.blob, report.fileName)
      return
    }
    setMIMETypeValue('application/pdf')
    setFile((window.URL || window.webkitURL).createObjectURL(report.blob))
    setFileName('referto.pdf')
  }

  const rowActions : Action[] = [
    {
      id: 'download',
      label: 'Scarica referto',
      icon: mdiDownload,
      action: handleModalView,
      condition: (row: any) => row.unsignedReportId || row.signedReportId
    },
    {
      id: 'download',
      label: 'Apri viewer DICOM',
      icon: mdiOpenInNew,
      action: handleOpenEcoExam,
      condition: (row: any) => row.studyUID
    }
  ]

  const fetchData = async () => {
    const fetchedData = await apiClient.getExams(patientId, filters, setStatus, pagination) ?? []
    const parsedData = fetchedData.map((exam: any) => {
      return {
        ...exam,
        reportingDoctor: exam.reportingDoctor && `${exam.reportingDoctor.firstName} ${exam.reportingDoctor.lastName}`,
        requestingUser: exam.requestingUser && `${exam.requestingUser.firstName} ${exam.requestingUser.lastName}`,
        state: exam?.signedReportId ? 'Completato' : 'Assente'
      }
    })
    setData(parsedData)
    const fetchedDataCount = await apiClient.getExamsCount(patientId, filters, setStatusCount) ?? []
    setCount(fetchedDataCount)
  }

  const saveEchocardiographicRequest = async (values: any) => {
    setPostMessage("Errore nel richiedere l'esame")
    await apiClient.postExamRequest(values, setStatusPost)
    await fetchData()
    setIsRequestModal(false)
  }

  useEffect(() => {
    fetchData()
  }, [filters, pagination])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) {
      setOpenViewModal(false)
    }
  }, [statusDownload])

  const onSubmit = (values: any) => {
    setFilters(values)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'end'}}>
      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
        <Filters config={filterConfig} key='filters' onSubmit={onSubmit} />
      </div>

      <TableCustom
        actions={rowActions}
        columns={columns}
        onPaginationChange={setPagination}
        pagination={pagination}
        rows={data}
        totalElements={count}
      />
      {isRequestModal &&
        <EchocardiographicRequestDialog
          onClose={() => setIsRequestModal(false)}
          onSave={saveEchocardiographicRequest}
        />}
      <FileViewerModal
        MIMETypeValue={MIMETypeValue}
        fileName={fileName}
        filepath={file}
        handleClose={() => { setOpenViewModal(false); setFile(undefined); setMIMETypeValue(undefined); setFileName(undefined) }}
        open={openViewModal}
        status={statusDownload}
      />
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatus(undefined); setStatusCount(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {'Errore nel caricare i dati della pagina.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatusDelete(undefined) }}
        open={statusDelete === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatusDelete(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {'Errore nel cancellare.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatusPost(undefined) }}
        open={statusPost === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatusPost(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {postMessage}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatusViewer(undefined) }}
        open={statusViewer === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatusViewer(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {viewerMessage}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </div>
  )
}

export default Exams
